import { useTranslation } from 'next-i18next';

import { useCallback } from 'react';

import { Box, Button, Flex, HStack, Spinner, Text, useBreakpointValue } from '@chakra-ui/react';

import isEmpty from 'lodash/isEmpty';

import { FeatureToggleEnum, LoadingState } from '@nocowanie/core';

import { ApartmentBookButton } from '@app/components/atoms/apartment-book-button';
import { GuestsNightsLabel } from '@app/components/atoms/guests-nights-label';
import { GuestsPictograms } from '@app/components/atoms/guests-pictograms';
import { RoomPictograms } from '@app/components/atoms/room-pictograms';
import { UnavailabilityCTA } from '@app/components/atoms/unavailability-cta';
import { ApartmentHeader } from '@app/components/molecules/apartment-header';
import { Price } from '@app/components/molecules/price';
import { ProvisionGuaranteeInfo } from '@app/components/molecules/provision-guarantee-info';
import { ApartmentSkeleton } from '@app/components/organisms/apartment-skeleton';
import { AppModalsEnum } from '@app/enums';
import { useDrawerState } from '@app/hooks';
import { apiTypes } from '@app/models';
import { accommodationSlice, commonUiStateSlice, useAppDispatch, useAppSelector } from '@app/state';

import { BestOfferProps } from './best-offer.props';

export const BestOffer = ({
    showHeading = true,
    withMoreDetails = false,
    apartmentBookButtonProps,
    ...props
}: BestOfferProps) => {
    const { t } = useTranslation('accommodation');
    const dispatch = useAppDispatch();
    const USE_SHORT_JOURNEY = useAppSelector(
        commonUiStateSlice.selectors.selectUIFtActive<boolean>(FeatureToggleEnum.USE_SHORT_JOURNEY),
    );
    const currentAccommodation = useAppSelector(
        accommodationSlice.selectors.selectCurrentAccommodation,
    );
    const bestOffer = useAppSelector(accommodationSlice.selectors.selectBestOffer);
    const firstApartment = currentAccommodation?.apartments?.[0];

    const bestOfferImage = useAppSelector(
        accommodationSlice.selectors.selectApartmentImage(firstApartment?.id),
    );

    const loadingState = useAppSelector(accommodationSlice.selectors.selectLoading);
    const isLoading = loadingState === LoadingState.PENDING;
    const isMobileRes = useBreakpointValue({
        base: true,
        md: false,
    });
    const skeletonBodyLineCount = useBreakpointValue({
        md: 4,
        base: 2,
    });

    const handleShowApartmentDetails = useCallback(() => {
        dispatch(
            commonUiStateSlice.actions.setModalDataAction({
                modalName: AppModalsEnum.ApartmentDetails,
                modalData: firstApartment,
            }),
        );
    }, [firstApartment]);

    const handleShowReservationDetails = useCallback(() => {
        dispatch(
            commonUiStateSlice.actions.setModalDataAction({
                modalName: AppModalsEnum.CancellationDetails,
                modalData: {
                    apartmentId: `${firstApartment?.id}`,
                    variant: bestOffer,
                },
            }),
        );
    }, [firstApartment, bestOffer]);

    const { isDrawerPending: isApartmentDrawerPending, openDrawer: openApartmentDrawer } =
        useDrawerState(handleShowApartmentDetails);

    const {
        isDrawerPending: isReservationDetailsDrawerPending,
        openDrawer: openReservationDetailsDrawer,
    } = useDrawerState(handleShowReservationDetails);

    if (isLoading) {
        return (
            <ApartmentSkeleton
                withMoreDetails={withMoreDetails}
                isBestOffer={showHeading ? true : false}
                bodyLineCount={withMoreDetails ? 6 : skeletonBodyLineCount}
                hideTitle={isMobileRes}
            />
        );
    }

    if (isEmpty(currentAccommodation)) {
        return;
    }

    return !bestOffer ? (
        <UnavailabilityCTA direction={'column'} buttonProps={{ minW: '100%' }} />
    ) : (
        <>
            <Box data-testid="best-offer" {...props}>
                <Flex
                    direction={{
                        base: withMoreDetails ? 'column' : 'row',
                        md: 'column',
                    }}
                    justify={'space-between'}
                >
                    <Box>
                        {showHeading ? (
                            <Text
                                as="h4"
                                fontWeight={'bold'}
                                textTransform={'uppercase'}
                                color={'green.solid'}
                                fontSize={'xs'}
                                mb={3}
                            >
                                {t('bestOffer')}
                            </Text>
                        ) : null}
                        <ApartmentHeader
                            name={firstApartment?.name ?? ''}
                            showDetails
                            imageSrc={withMoreDetails ? bestOfferImage : undefined}
                            lineClamp={withMoreDetails ? 4 : undefined}
                            onClick={openApartmentDrawer}
                            isDrawerPending={isApartmentDrawerPending}
                        />
                        {withMoreDetails ? (
                            <>
                                <RoomPictograms
                                    size={firstApartment?.size}
                                    singleBeds={firstApartment?.singleBeds}
                                    doubleBeds={firstApartment?.doubleBeds}
                                    iconSize="sm"
                                    my={4}
                                />
                                <GuestsPictograms
                                    adultsCount={bestOffer.guests}
                                    childrenCount={bestOffer.childrenAges.length}
                                />
                            </>
                        ) : null}
                    </Box>

                    <HStack justify={'space-between'} align={'flex-end'}>
                        <Box mt={3} hideBelow={!withMoreDetails ? 'md' : undefined}>
                            <ProvisionGuaranteeInfo
                                provision={bestOffer.provision}
                                price={bestOffer.convertedTotalPrice}
                                cancellationRules={bestOffer.cancellationRules}
                            />
                            <Button
                                justifyContent="flex-start"
                                variant="link"
                                colorPalette="secondary"
                                size={'sm'}
                                onClick={openReservationDetailsDrawer}
                                data-testid="reservation-details-button"
                                mt={2}
                            >
                                {t('cancellation.reservationDetailsButton')}
                                {isReservationDetailsDrawerPending ? (
                                    <Spinner ml={2} size={'xs'} />
                                ) : null}
                            </Button>
                        </Box>
                        <Price
                            currency={currentAccommodation?.convertedRatesCurrency ?? ''}
                            price={bestOffer.convertedTotalPrice ?? 0}
                            basePrice={bestOffer.convertedTotalBasePrice ?? 0}
                            discounts={bestOffer.discounts as apiTypes.Discount[]}
                        >
                            <GuestsNightsLabel
                                adultsCount={bestOffer.guests}
                                childrenCount={bestOffer.childrenAges.length}
                            />
                        </Price>
                    </HStack>
                </Flex>
                {!isMobileRes || !USE_SHORT_JOURNEY || withMoreDetails ? (
                    <ApartmentBookButton
                        apartmentVariant={bestOffer}
                        apartmentId={firstApartment?.id ?? ''}
                        variant={withMoreDetails ? 'outline' : 'solid'}
                        width={'100%'}
                        mt={4}
                        data-testid="best-offer-reserve-button"
                        {...apartmentBookButtonProps}
                    />
                ) : null}
                <Text
                    textAlign={'center'}
                    color={'gray.500'}
                    fontSize={'sm'}
                    mt={2}
                    hideBelow={'md'}
                >
                    {t('nextActionInfo')}
                </Text>
            </Box>
        </>
    );
};
